import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, setActiveOrg } from "./lib/api.js";
import { authClient, useSession } from "./lib/auth.js";
import { navigate, usePath } from "./lib/router.js";
import { AuthGate } from "./routes/AuthGate.js";
import { ComposeView } from "./routes/Compose.js";
import { ConnectionsView } from "./routes/Connections.js";
import { PostsView } from "./routes/Posts.js";

const NAV = [
  { path: "/compose", label: "Compose" },
  { path: "/posts", label: "Posts" },
  { path: "/connections", label: "Connections" },
];

export function App() {
  const { data: session, isPending } = useSession();
  if (isPending) return <div className="center">Loading…</div>;
  if (!session) return <AuthGate />;
  return <Shell />;
}

function Shell() {
  const path = usePath();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const [orgId, setOrgId] = useState<string | null>(null);

  useEffect(() => {
    if (me.data) {
      const id = me.data.activeOrgId ?? me.data.organizations[0]?.id ?? null;
      setOrgId(id);
      setActiveOrg(id);
    }
  }, [me.data]);

  if (me.isLoading) return <div className="center">Loading workspace…</div>;
  if (me.data && me.data.organizations.length === 0) return <CreateOrg onDone={() => me.refetch()} />;

  return (
    <div className="app">
      <nav className="nav">
        <strong>Content Publisher</strong>
        <div style={{ margin: "1rem 0" }}>
          {NAV.map((n) => (
            <a
              key={n.path}
              href={n.path}
              className={path.startsWith(n.path) ? "active" : ""}
              onClick={(e) => {
                e.preventDefault();
                navigate(n.path);
              }}
            >
              {n.label}
            </a>
          ))}
        </div>
        {me.data && me.data.organizations.length > 1 && (
          <select
            value={orgId ?? ""}
            onChange={(e) => {
              setOrgId(e.target.value);
              setActiveOrg(e.target.value);
            }}
          >
            {me.data.organizations.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        )}
        <p className="muted" style={{ marginTop: "2rem" }}>{me.data?.user.email}</p>
      </nav>
      <main className="main">
        {path.startsWith("/connections") && <ConnectionsView />}
        {path.startsWith("/compose") && <ComposeView />}
        {(path === "/" || path.startsWith("/posts")) && <PostsView />}
      </main>
    </div>
  );
}

function CreateOrg({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="center">
      <h2>Create your workspace</h2>
      <label>Workspace name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Social" />
      <button
        style={{ marginTop: "1rem" }}
        disabled={busy || name.length < 2}
        onClick={async () => {
          setBusy(true);
          const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
          await authClient.organization.create({ name, slug: slug || `w-${Date.now()}` });
          setBusy(false);
          onDone();
        }}
      >
        Create
      </button>
    </div>
  );
}
